import type { Map as MapLibreMap } from 'maplibre-gl'

import {
  fetchManifest,
  findModel,
  vectorUrl,
  type RunManifest,
  type VectorKind,
} from '../../api/models'
import type { Layer } from '../layer'
import { drawBarb, glyphKeys, glyphName } from './barbGlyph'
import { GeoJsonFramesLayer, type VectorFrame } from './geoJsonFramesLayer'
import { ink, inkTone } from './ink'
import { pickForecastSteps, type LeadWindow } from './modelLayer'

/**
 * A model field drawn as lines or arrows: `GeoJsonFramesLayer` wired to a run
 * manifest, exactly as `modelLayer.ts` wires `ImageFramesLayer`.
 *
 * It reuses `pickForecastSteps`, the lead window, the run selector and
 * `timeline: 'run'` unchanged, because a contour step and a plate step are the
 * same step — one run, one lead list, one clock. What differs is only what is
 * drawn at each of them.
 *
 * Nothing here knows what a hectopascal is. The interval, the units and the
 * emphasis rule come from `/api/models`, because the backend registry is what
 * generated the geometry.
 */

/** Isobars and height contours are ink over geography rather than labels, so
 *  they follow the standing vector rule's *first* half and go **under** the
 *  basemap's names. Value labels, if they are ever placed along the line,
 *  would follow the second half and go above. This phase places none.
 *  Their colour is `ink()`, which follows the basemap's tone. */

/** The tone each map's barb images were last drawn in. */
const glyphTones = new WeakMap<MapLibreMap, string>()

export interface ModelVectorLayerOptions {
  id: string
  model: string
  variable: string
  kind: VectorKind
  attribution: string
  opacity: number
  frameCount: number
  window?: LeadWindow
  run?: string | null
  /** Tests pass a fixed clock; nothing else does. */
  now?: () => number
}

export class ModelVectorLayer implements Layer {
  readonly id: string
  private inner: GeoJsonFramesLayer
  private run: string | null = null
  private manifest: RunManifest | null = null
  private byTime = new Map<number, number>()

  constructor(private readonly opts: ModelVectorLayerOptions) {
    this.id = opts.id
    this.inner = new GeoJsonFramesLayer({
      id: opts.id,
      attribution: opts.attribution,
      opacity: opts.opacity,
      loadFrames: () => this.resolveFrames(),
      addLayers: (map, sourceId, opacity, beforeId) =>
        opts.kind === 'barb'
          ? this.addBarbLayers(map, sourceId, opacity, beforeId)
          : this.addContourLayers(map, sourceId, opacity, beforeId),
    })
  }

  get times(): readonly Date[] {
    return this.inner.times
  }

  currentTime(): Date | null {
    return this.inner.currentTime()
  }

  /** The run on screen, or null before `load()` lands. `probe.ts` asks. */
  currentRun(): string | null {
    return this.run
  }

  /** The lead the layer is *drawing*, so a readout describes what is on
   *  screen rather than where the slider is — the same discipline
   *  `ModelLayer` follows, and exact for the same reason. */
  currentLead(): number | null {
    const time = this.currentTime()
    return time ? (this.byTime.get(time.getTime()) ?? null) : null
  }

  currentManifest(): RunManifest | null {
    return this.manifest
  }

  private async resolveFrames(): Promise<{ frames: VectorFrame[]; attribution: string } | null> {
    const info = await findModel(this.opts.model)
    if (!info) return null
    const chosen = this.opts.run
    const run = chosen && info.runs.some((r) => r.run === chosen) ? chosen : info.latest
    if (!run) return null

    const variable = info.variables.find((v) => v.id === this.opts.variable)
    // A kind the deployment does not write has no artifacts, and a layer that
    // turns on and draws nothing is indistinguishable from a quiet day. The
    // catalog says which kinds exist; this refuses rather than guessing.
    if (!variable?.vectors?.includes(this.opts.kind)) return null

    const manifest = await fetchManifest(this.opts.model, run)
    const entry = manifest?.variables?.[this.opts.variable]
    if (!manifest || !entry?.steps?.length) return null
    // The *run* has to hold them too: one ingested before the field grew a
    // spec has plates and no contours, and the manifest is what says so.
    if (entry.vectors && !entry.vectors.includes(this.opts.kind)) return null

    const picked = pickForecastSteps(
      entry.steps,
      this.opts.frameCount,
      this.opts.now?.() ?? Date.now(),
      { window: this.opts.window, stridable: (variable.window_minutes ?? 0) === 0 },
    )
    if (picked.length === 0) return null

    this.run = run
    this.manifest = manifest
    this.byTime = new Map(picked.map((step) => [step.time.getTime(), step.lead]))
    return {
      frames: picked.map((step) => ({
        time: step.time,
        url: vectorUrl(this.opts.model, run, this.opts.variable, this.opts.kind, step.lead),
      })),
      attribution: info.attribution,
    }
  }

  /**
   * Two line layers rather than one with a data-driven width.
   *
   * A width expression would work; two layers is what lets the heavy lines sit
   * *above* the light ones, so a 1000 hPa isobar is not interrupted wherever a
   * 1004 crosses in front of it. `emphasis` is the backend's answer and
   * arrives per feature as `bold`.
   */
  private addContourLayers(
    map: MapLibreMap,
    sourceId: string,
    opacity: number,
    beforeId?: string,
  ): string[] {
    const plain = `${sourceId}:line`
    const bold = `${sourceId}:line-bold`
    map.addLayer(
      {
        id: plain,
        type: 'line',
        source: sourceId,
        filter: ['!', ['get', 'bold']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ink().line, 'line-width': 1, 'line-opacity': opacity },
      },
      beforeId,
    )
    map.addLayer(
      {
        id: bold,
        type: 'line',
        source: sourceId,
        filter: ['get', 'bold'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ink().bold, 'line-width': 2, 'line-opacity': opacity },
      },
      beforeId,
    )
    // Top down, for removal.
    return [bold, plain]
  }

  /**
   * One symbol layer, drawing registered images by the key the backend rounded.
   *
   * `icon-allow-overlap: false` is what gives zoom-dependent density for free:
   * MapLibre's own collision detection thins the lattice at low zoom and shows
   * all of it at high, so one emitted lattice serves every zoom above the
   * density it was written at. That is the roadmap's "zoom-dependent density"
   * without three files per step.
   *
   * `icon-rotation-alignment: 'map'` or the barbs stay upright while the map
   * rotates, which would make every one of them wrong.
   */
  private addBarbLayers(
    map: MapLibreMap,
    sourceId: string,
    opacity: number,
    beforeId?: string,
  ): string[] {
    this.registerGlyphs(map)
    const id = `${sourceId}:barb`
    map.addLayer(
      {
        id,
        type: 'symbol',
        source: sourceId,
        layout: {
          'icon-image': ['concat', 'barb-', ['to-string', ['get', 'k']]],
          'icon-rotate': ['get', 'd'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': false,
          'icon-ignore-placement': false,
          'icon-size': 0.8,
        },
        paint: { 'icon-opacity': opacity },
      },
      beforeId,
    )
    return [id]
  }

  /**
   * Draw and register every glyph once per map.
   *
   * A basemap switch destroys the style and its images with it, so this runs
   * on every mount and skips what is already there — `hasImage` rather than a
   * flag, because the style is what actually holds them.
   *
   * The glyphs are drawn in the basemap's ink, so a map whose images were
   * drawn for the other tone has them replaced: whether a style switch keeps
   * its images is MapLibre's business, and a dark barb surviving onto the
   * dark map would be an invisible one.
   */
  private registerGlyphs(map: MapLibreMap): void {
    const ratio = typeof devicePixelRatio === 'number' ? devicePixelRatio : 1
    const tone = inkTone()
    const stale = glyphTones.get(map) !== tone
    glyphTones.set(map, tone)
    for (const key of glyphKeys()) {
      const name = glyphName(key)
      if (map.hasImage?.(name)) {
        if (!stale) continue
        map.removeImage?.(name)
      }
      const image = drawBarb(key, ink().barb, ratio)
      // No canvas rasteriser (jsdom) means no icons and no exception: the
      // source and the layer still mount, which is what the registry test
      // needs and what a headless environment can offer.
      if (!image) continue
      map.addImage(name, image, { pixelRatio: image.pixelRatio })
    }
  }

  load(): Promise<void> {
    return this.inner.load()
  }

  add(map: Parameters<Layer['add']>[0], beforeId?: string): void {
    this.inner.add(map, beforeId)
  }

  remove(map: Parameters<Layer['remove']>[0]): void {
    this.inner.remove(map)
  }

  setOpacity(opacity: number): void {
    this.inner.setOpacity(opacity)
  }

  setTime(time: Date): void {
    this.inner.setTime(time)
  }

  setVisible(visible: boolean): void {
    this.inner.setVisible(visible)
  }

  ready(): Promise<void> {
    return this.inner.ready()
  }
}